import React, { useState, useEffect } from 'react';
import { FeedbackForm as FeedbackFormType, FeedbackResponse, FeedbackSubmission } from '../../types/feedback';
import DynamicQuestion from './DynamicQuestion';
import { feedbackService } from '../../services/feedbackService';

interface FeedbackFormProps {
  form: FeedbackFormType;
  onSubmitSuccess: (response: any) => void;
  onSubmitError: (error: string) => void;
  onClose: () => void;
}

interface FormErrors {
  [questionId: string]: string;
}

const QUESTIONS_PER_PAGE = 5;

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  form,
  onSubmitSuccess,
  onSubmitError,
  onClose
}) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  // Calculate pagination
  const totalQuestions = form.questions.length;
  const totalPages = Math.ceil(totalQuestions / QUESTIONS_PER_PAGE);
  const showPagination = totalQuestions > QUESTIONS_PER_PAGE;
  
  // Get current page questions
  const getCurrentPageQuestions = () => {
    const startIndex = currentPage * QUESTIONS_PER_PAGE;
    const endIndex = startIndex + QUESTIONS_PER_PAGE;
    return form.questions.slice(startIndex, endIndex);
  };

  // Initialize form data with default values
  useEffect(() => {
    const initialData: { [key: string]: any } = {};
    form.questions.forEach(question => {
      switch (question.type) {
        case 'checkbox':
          initialData[question.id] = [];
          break;
        case 'rating':
          initialData[question.id] = 0;
          break;
        default:
          initialData[question.id] = '';
      }
    });
    setFormData(initialData);
  }, [form]);

  const handleQuestionChange = (questionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Clear error when user starts typing
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: ''
      }));
    }
  };

  const validateCurrentPage = (): boolean => {
    const newErrors: FormErrors = {};
    const currentPageQuestions = getCurrentPageQuestions();

    currentPageQuestions.forEach(question => {
      const value = formData[question.id];

      // Skip required field validation - all fields are now optional
      // Only do type-specific validation if there's a value
      if (value && question.validation) {
        const validation = question.validation;

        // String length validation
        if (typeof value === 'string') {
          if (validation.minLength && value.length < validation.minLength) {
            newErrors[question.id] = `Minimum ${validation.minLength} characters required`;
          }
          if (validation.maxLength && value.length > validation.maxLength) {
            newErrors[question.id] = `Maximum ${validation.maxLength} characters allowed`;
          }
        }

        // Number validation
        if (typeof value === 'number') {
          if (validation.min !== undefined && value < validation.min) {
            newErrors[question.id] = `Minimum value is ${validation.min}`;
          }
          if (validation.max !== undefined && value > validation.max) {
            newErrors[question.id] = `Maximum value is ${validation.max}`;
          }
        }

        // Email validation
        if (question.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            newErrors[question.id] = 'Please enter a valid email address';
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Next button clicked, current page:', currentPage);
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const handleBack = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
    // Clear errors when going back
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if we're on the last page
    if (currentPage !== totalPages - 1) {
      console.log('Not on last page, preventing submission');
      return;
    }

    console.log('Submitting form from last page');
    setIsSubmitting(true);

    try {
      // Build responses object
      const responses: FeedbackResponse = {};
      form.questions.forEach(question => {
        const value = formData[question.id];
        if (value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
          responses[question.id] = {
            question: question.question,
            answer: value,
            type: question.type
          };
        }
      });

      // Build submission data
      const submissionData: FeedbackSubmission = {
        feedbackType: form.feedbackType,
        title: form.title,
        description: form.description,
        responses,
        email: formData.contact_email || undefined
      };

      const result = await feedbackService.submitFeedback(submissionData);

      if (result.success) {
        onSubmitSuccess(result);
      } else {
        onSubmitError(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      onSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentPageQuestions = getCurrentPageQuestions();
  const isLastPage = currentPage === totalPages - 1;
  const isFirstPage = currentPage === 0;

  return (
    <div className="feedback-form-container">
      {/* Sticky Header */}
      <div className="form-header sticky-header">
        <h2 className="form-title">{form.title}</h2>
        {form.description && (
          <p className="form-description">{form.description}</p>
        )}
        
        {showPagination && (
          <div className="progress-container">
            <div className="progress-info">
              <span className="progress-text">
                Step {currentPage + 1} of {totalPages}
              </span>
              <span className="progress-questions">
                Questions {currentPage * QUESTIONS_PER_PAGE + 1}-{Math.min((currentPage + 1) * QUESTIONS_PER_PAGE, totalQuestions)} of {totalQuestions}
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="feedback-form">
        <div className="form-content scrollable-content">
          {currentPageQuestions.map((question, index) => (
            <div key={question.id} className="question-wrapper">
              <DynamicQuestion
                question={question}
                value={formData[question.id]}
                onChange={(value) => handleQuestionChange(question.id, value)}
                error={errors[question.id]}
              />
            </div>
          ))}
        </div>

        {/* Sticky Footer */}
        <div className="form-footer sticky-footer">
          <div className="form-actions">
            <div className="form-navigation">
              {showPagination && !isFirstPage && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  ← Back
                </button>
              )}
              
              <button
                type="button"
                onClick={onClose}
                className="btn btn-ghost"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>

            <div className="form-submit">
              {showPagination && !isLastPage ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  Next →
                </button>
              ) : (
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Transmitting...' : (form.submitText || 'Submit Feedback')}
                  </button>
                </form>
              )}
            </div>
          </div>

          {showPagination && (
            <div className="pagination-dots">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`dot ${index === currentPage ? 'active' : ''} ${index < currentPage ? 'completed' : ''}`}
                  onClick={() => setCurrentPage(index)}
                  disabled={isSubmitting}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
